import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Heart, Eye, Calendar, Play, ShoppingCart, User, ArrowLeft, Share2, Download } from 'lucide-react';
import { Video, VideoWithRelated } from '@/types/video';
import { useAuth } from '@/contexts/AuthContext';
import { generateEmbedUrl, formatDuration } from '@/utils/videoUtils';
import { getBestSongName, extractArtistName } from '@/utils/songUtils';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import VideoSpotifyMetadata from '@/components/videos/VideoSpotifyMetadata';
import VideoLikedBy from '@/components/videos/VideoLikedBy';
import { useVideoLikes } from '@/hooks/useVideoLikes';

const VideoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<VideoWithRelated | null>(null);
  const [loading, setLoading] = useState(true);
  const [liking, setLiking] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  
  // Hook for fetching video likes
  const { data: likesData, loading: likesLoading, refetch: refetchLikes } = useVideoLikes(id || '', 5);

  useEffect(() => {
    if (!id) return;
    
    fetchVideo();
    incrementView();
  }, [id]);

  const fetchVideo = async () => {
    try {
      setLoading(true);
      const headers: Record<string, string> = {};
      
      if (user) {
        const token = localStorage.getItem('supabase.auth.token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }

      const response = await fetch(`/api/videos/${id}`, { headers });
      const result = await response.json();
      
      if (response.ok) {
        setData(result);
        setIsLiked(result.video.is_liked || false);
        setLikeCount(result.video.like_count);
      } else {
        toast.error('Video not found');
        navigate('/videos');
      }
    } catch (error) {
      console.error('Error fetching video:', error);
      toast.error('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const incrementView = async () => {
    try {
      await fetch(`/api/videos/${id}/view`, { method: 'POST' });
    } catch (error) {
      console.error('Error incrementing view:', error);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please log in to like videos');
      return;
    }

    if (liking) return;

    try {
      setLiking(true);
      const token = localStorage.getItem('supabase.auth.token');
      const response = await fetch(`/api/videos/${id}/like`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (response.ok) {
        setIsLiked(result.is_liked);
        setLikeCount(result.like_count);
        // Refetch the likes data to update the "liked by" section
        refetchLikes();
      } else {
        toast.error('Failed to like video');
      }
    } catch (error) {
      console.error('Error liking video:', error);
      toast.error('Failed to like video');
    } finally {
      setLiking(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: data?.video.title,
          url: url
        });
      } catch (error) {
        // User cancelled sharing
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      } catch (error) {
        toast.error('Failed to copy link');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <NavBar />
        <main className="flex-1 container mx-auto px-4 py-6 pt-24">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Video Player */}
              <div className="lg:col-span-2">
                <Skeleton className="aspect-video w-full mb-4" />
                <Skeleton className="h-8 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-4" />
                <div className="flex gap-2">
                  <Skeleton className="h-10 w-20" />
                  <Skeleton className="h-10 w-20" />
                </div>
              </div>
              
              {/* Sidebar */}
              <div className="space-y-6">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <NavBar />
        <main className="flex-1 container mx-auto px-4 py-6 pt-24">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold mb-4">Video not found</h1>
            <Button asChild>
              <Link to="/videos">Back to Videos</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { video, related_videos } = data;
  const embedUrl = generateEmbedUrl(video.video_platform, video.video_id);
  
  // Get the best available song name from multiple sources
  const displaySongName = getBestSongName(video.song_name, video.sequence?.title);
  const displayArtistName = video.artist_name || extractArtistName(video.sequence?.title || '') || undefined;

  return (
    <>
      <Helmet>
        <title>{video.title} | LightsHunt Videos</title>
        <meta name="description" content={video.description || `Watch this amazing ${video.holiday} light display video`} />
        <meta name="keywords" content={`${video.holiday}, holiday lights, light display, ${video.song_name}, ${video.artist_name}`} />
        <meta property="og:title" content={video.title} />
        <meta property="og:description" content={video.description || `Watch this amazing ${video.holiday} light display video`} />
        <meta property="og:image" content={video.thumbnail_url} />
        <meta property="og:type" content="video.other" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <NavBar />
        
        <main className="flex-1 container mx-auto px-4 py-6 pt-24">
          <div className="max-w-7xl mx-auto">
            {/* Back Button */}
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {/* Video Player */}
                <div className="aspect-video bg-black rounded-lg overflow-hidden mb-6">
                  <iframe
                    src={embedUrl}
                    title={video.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>

                {/* Video Info */}
                <div className="space-y-4">
                  <div>
                    <h1 className="text-2xl font-bold mb-2">{video.title}</h1>
                    
                    {/* Creator and Stats */}
                    <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="h-4 w-4" />
                          <span>{video.user?.email || 'Unknown User'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDistanceToNow(new Date(video.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1 text-sm">
                          <Eye className="h-4 w-4" />
                          {video.view_count.toLocaleString()} views
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mb-4">
                      <Button
                        variant={isLiked ? "default" : "outline-solid"}
                        onClick={handleLike}
                        disabled={liking}
                        className="flex items-center gap-2"
                      >
                        <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
                        {likeCount}
                      </Button>
                      
                      <Button variant="outline" onClick={handleShare}>
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </Button>
                    </div>

                    {/* Liked By Section */}
                    {likesData && likesData.totalLikes > 0 && (
                      <div className="mb-6">
                        <VideoLikedBy
                          videoId={id || ''}
                          likes={likesData.likes}
                          totalLikes={likesData.totalLikes}
                          loading={likesLoading}
                          variant="full"
                          maxAvatars={4}
                        />
                      </div>
                    )}

                    {/* Description */}
                    {video.description && (
                      <div className="bg-muted p-4 rounded-lg">
                        <p className="text-sm whitespace-pre-wrap">{video.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Sequence Purchase */}
                {(video.sequence || video.sequence_id) && (
                  <Card className="border-primary">
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Download className="h-4 w-4" />
                        Get This Sequence
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Download the sequence file to recreate this display
                      </p>
                      <div className="space-y-3">
                        <div>
                          <div className="font-medium">
                            {video.sequence?.title || displaySongName || 'Light Sequence'}
                          </div>
                          <div className="text-lg font-bold">
                            {video.sequence?.is_free || video.sequence?.price === '0' || video.sequence?.price === 0 
                              ? 'Free Download' 
                              : `$${(video.sequence?.price ?? 0).toFixed(2)}`}
                          </div>
                        </div>
                        <Button asChild className="w-full">
                          <Link to={`/sequence/${video.sequence?.id || video.sequence_id}`}>
                            <Download className="h-4 w-4 mr-2" />
                            Download Now
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Spotify Song Metadata */}
                <VideoSpotifyMetadata
                  spotifyTrackId={video.spotify_track_id}
                  songName={displaySongName}
                  artistName={displayArtistName}
                  albumName={video.album_name}
                  albumCoverUrl={video.sequence?.album_cover_url}
                  thumbnailUrl={video.thumbnail_url}
                  duration={video.duration}
                  spotifyDurationMs={video.sequence?.spotify_duration_ms}
                  spotifyPopularity={video.sequence?.spotify_popularity}
                  spotifyExplicit={video.sequence?.spotify_explicit}
                  spotifyPreviewUrl={video.sequence?.spotify_preview_url}
                />

                {/* Video Metadata */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3">Video Details</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Platform:</span>
                        <Badge variant="secondary" className="capitalize">
                          {video.video_platform}
                        </Badge>
                      </div>
                      
                      {video.duration && (
                        <div className="flex justify-between">
                          <span>Duration:</span>
                          <span>{formatDuration(video.duration)}</span>
                        </div>
                      )}
                      
                      {video.holiday && (
                        <div className="flex justify-between">
                          <span>Holiday:</span>
                          <Badge variant="outline">{video.holiday}</Badge>
                        </div>
                      )}
                      
                      {video.display_year && (
                        <div className="flex justify-between">
                          <span>Display Year:</span>
                          <span>{video.display_year}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Related Videos */}
                {related_videos.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-4">Related Videos</h3>
                      <div className="space-y-3">
                        {related_videos.slice(0, 4).map((relatedVideo) => (
                          <Link
                            key={relatedVideo.id}
                            to={`/videos/${relatedVideo.id}`}
                            className="block group"
                          >
                            <div className="flex gap-3">
                              <div className="relative w-24 h-16 bg-muted rounded overflow-hidden shrink-0">
                                {relatedVideo.thumbnail_url ? (
                                  <img
                                    src={relatedVideo.thumbnail_url}
                                    alt={relatedVideo.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-linear-to-br from-muted to-muted-foreground/20 flex items-center justify-center">
                                    <Play className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                )}
                                {relatedVideo.duration && (
                                  <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1 rounded">
                                    {formatDuration(relatedVideo.duration)}
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                                  {relatedVideo.title}
                                </h4>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {relatedVideo.view_count.toLocaleString()} views
                                </p>
                                {relatedVideo.song_name && (
                                  <p className="text-xs text-muted-foreground line-clamp-1">
                                    {relatedVideo.song_name}
                                  </p>
                                )}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default VideoDetail;